import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button, Paper, CircularProgress } from '@mui/material';
import useDealStore from '../../stores/dealStore';

const DealStoryView = ({ tenantId, dealId }) => {
  // Use deal store for story data and loading state
  const dealStory = useDealStore((state) => state.dealStory);
  const loading = useDealStore((state) => state.loading.dealStory);
  const fetchDealStory = useDealStore((state) => state.fetchDealStory);
  const generateDealStory = useDealStore((state) => state.generateDealStory);
  
  // Keep error in local state since it's UI-specific
  const [error, setError] = useState(null);

  // Load existing story on component mount
  useEffect(() => {
    const loadExistingStory = async () => {
      try {
        await fetchDealStory(tenantId, dealId);
      } catch (err) {
        console.error('Error fetching existing story:', err);
        // Don't show error for initial fetch - just silently fail
      }
    };
    
    loadExistingStory();
  }, [tenantId, dealId, fetchDealStory]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  
  // Generate story on button click
  const generateStory = async () => {
    setError(null);
    try {
      await generateDealStory(tenantId, dealId);
    } catch (err) {
      console.error('Error generating story:', err);
      setError('Failed to generate executive summary. Please try again.');
    }
  };
  
  return (
    <Paper sx={{ p: 2, mb: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Executive Summary</Typography>
        <Button 
          variant="contained"
          onClick={generateStory}
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} color="inherit" />}
        >
          {loading ? 'Generating...' : dealStory ? 'Regenerate Summary' : 'Generate Summary'}
        </Button>
      </Box>
      
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      
      {dealStory ? (
        <>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
            Generated on {formatDate(dealStory.generated_at)}
          </Typography>
          
          <Box sx={{ whiteSpace: 'pre-wrap' }}>
            {dealStory.content.split('\n\n').map((paragraph, idx) => (
              <Typography key={idx} paragraph>
                {paragraph}
              </Typography>
            ))}
          </Box>
        </>
      ) : (
        <Typography color="text.secondary">
          No executive summary available. Click the button to generate one based on the deal information.
        </Typography>
      )}
    </Paper>
  );
};

DealStoryView.propTypes = {
  tenantId: PropTypes.string.isRequired,
  dealId: PropTypes.string.isRequired
};

export default DealStoryView;
